import React, { useMemo, useState } from 'react';
import { AdminBarChart, DistributionList } from '../../components/admin/AdminChart';
import { AdminEmptyState, AdminMetricCard, AdminPageTitle, DateRange, Panel } from '../../components/admin/AdminPrimitives';
import { DataTable, type Column } from '../../components/admin/DataTable';
import { metricUsage, sportAdoption } from '../../data/admin';
import { sports } from '../../data/sportMetrics';
import { useLocale } from '../../contexts/LocaleContext';
import type { SportId } from '../../types';
interface Row {
  id: SportId;
  sport: string;
  users: number;
  adoption: number;
  frequency: number;
  trend: number;
}

/**
 * Sports analytics driven by the central sports catalogue (data/sportMetrics `sports`).
 * A sport added to the catalogue appears here automatically: with analytics when
 * aggregated data exists, otherwise listed under "sem dados ainda" — never with fake numbers.
 */
export function AdminSports() {
  const {
    t
  } = useLocale();
  const [range, setRange] = useState('90d');
  const {
    rows,
    withoutData
  } = useMemo(() => {
    const label = (id: SportId) => t(`category.${id}`);
    const withData: Row[] = [];
    const empty: SportId[] = [];
    sports.forEach(id => {
      const data = sportAdoption.find(entry => entry.sport === id);
      if (data) withData.push({
        ...data,
        id,
        sport: label(id)
      });else empty.push(id);
    });
    withData.sort((a, b) => b.users - a.users);
    return {
      rows: withData,
      withoutData: empty
    };
  }, [t]);
  const top = rows[0];
  const columns: Column<Row>[] = [{
    id: 'sport',
    header: 'Esporte',
    sortValue: row => row.sport,
    cell: row => <span className="text-[13px] font-medium text-deep" data-id="element-2063" data-mp="j6229qnu6mmp">{row.sport}</span>
  }, {
    id: 'users',
    header: 'Usuários ativos',
    align: 'right',
    sortValue: row => row.users,
    cell: row => <span className="bee-num font-bold text-deep" data-id="element-2064" data-mp="3rnop1jovcgb8">
          {row.users.toLocaleString('pt-BR')}
        </span>
  }, {
    id: 'adoption',
    header: 'Adoção',
    align: 'right',
    sortValue: row => row.adoption,
    cell: row => <span className="bee-num text-deep" data-id="element-2065" data-mp="3flf179dthrud">{row.adoption}%</span>
  }, {
    id: 'frequency',
    header: 'Frequência semanal',
    align: 'right',
    sortValue: row => row.frequency,
    cell: row => <span className="bee-num text-muted" data-id="element-2066" data-mp="30oeifvdlt781">{row.frequency.toFixed(1)}x</span>
  }, {
    id: 'trend',
    header: 'Tendência (90d)',
    align: 'right',
    sortValue: row => row.trend,
    cell: row => <span className={`bee-num font-bold ${row.trend >= 0 ? 'text-soft' : 'text-gold-deep'}`} data-id="element-2067" data-mp="3amu6tql8y2du">
          {row.trend >= 0 ? '+' : ''}
          {row.trend.toFixed(1)}%
        </span>
  }];
  return <div className="space-y-4" data-id="element-2068" data-mp="puidetz8j0tf">
      <AdminPageTitle title="Performance" subtitle="Adoção e uso das métricas esportivas, sempre em agregado. Nenhuma leitura aqui é interpretação clínica." action={<DateRange value={range} onChange={setRange} data-id="element-2070" data-mp="22wzmi5fx2k6u" />} data-id="element-2069" data-mp="kx8qbkzbj18r" />

      <section className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-id="element-2071" data-mp="27urhj8buymf9">
        <AdminMetricCard label="Usuários com esporte ativo" value="119.800" delta={4.2} hint="Ao menos uma métrica registrada" accent data-id="element-2072" data-mp="1tuqg9rrolcz9" />
        <AdminMetricCard label="Esporte mais adotado" value={top ? top.sport : '—'} hint={top ? `${top.adoption}% da base` : 'Sem dados ainda'} data-id="element-2073" data-mp="2mj4hege2gpf1" />
        <AdminMetricCard label="Frequência média" value="3,2x/semana" delta={1.6} data-id="element-2074" data-mp="2ct2qlgubwswf" />
        <AdminMetricCard label="Esportes no catálogo" value={String(sports.length)} hint={`${rows.length} com dados · ${withoutData.length} sem dados ainda`} data-id="element-2075" data-mp="2cqynv2861zxq" />
      </section>

      {rows.length === 0 ? <AdminEmptyState title="Sem dados esportivos ainda" body="Nenhum esporte do catálogo tem registros agregados no período." data-id="element-2076" data-mp="3a8oyetqntclo" /> : <>
          <DataTable rows={rows} columns={columns} rowKey={row => row.id} pageSize={12} data-id="element-2077" data-mp="1gnsyzwzwsscn" />

          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2" data-id="element-2078" data-mp="1clo3stttbnnw">
            <AdminBarChart title="Adoção por esporte" question="Qual modalidade concentra a base ativa?" data={rows} xKey="sport" series={[{
          id: 'users',
          name: 'Usuários ativos'
        }]} horizontal data-id="element-2079" data-mp="1co1xozcrattv" />
            <Panel title="Uso das métricas" hint="Proporção de usuários do esporte que preenchem cada métrica." data-id="element-2080" data-mp="20cbq4k4h6ftz">
              <DistributionList items={metricUsage.map(item => ({
            label: item.metric,
            value: item.usage
          }))} data-id="element-2081" data-mp="1qm8l1lk2xbfa" />
              <p className="mt-3 text-[11.5px] leading-snug text-muted" data-id="element-2082" data-mp="2o2fgvqwkel3k">
                Métricas com pouco preenchimento indicam atrito no registro, não ausência de
                prática.
              </p>
            </Panel>
          </div>
        </>}

      {withoutData.length > 0 && <Panel title="Esportes sem dados ainda" hint="Já fazem parte do catálogo. As análises aparecem automaticamente quando houver registros agregados." data-id="element-2083" data-mp="2vkvtnbzttao7">
          <ul className="flex flex-wrap gap-2" data-id="element-2084" data-mp="34m3ht18mov4k">
            {withoutData.map(id => <li key={id} className="rounded-pill border border-dashed border-deep/25 px-3 py-[6px] text-[12.5px] font-medium text-deep" data-id="element-2085" data-mp="39jd9yonejkw3">
                {t(`category.${id}`)}
                <span className="ml-1 text-muted" data-id="element-2086" data-mp="3jazujblmqwbi">· sem dados</span>
              </li>)}
          </ul>
        </Panel>}
    </div>;
}